import { useEffect, useRef, useState } from "react";

// Hooks
import { useNavigate } from "react-router-dom";
import { useSnackBar } from "components/SnackBar";
import { useAppDispatch, useAppSelector } from "app/hooks";
import useRedirectOnNoAdminPermission from "utils/hooks/useRedirectOnNoAdminPermission";

// Store
import { toggleLoader } from "store/slice/UI/UISlice";
import {
    fetchPermissionsRefBundleThunk,
    getPermissionsRefBundle,
} from "store/slice/sysadmin/permissions/permissionsSlice";

// Services
import { fetchClientHub, RoleBundle, RoleBundleResponse, TClientHubFiltersPayload } from "services/clienthub";

// Utils
import { useAuth } from "utils/hooks";
import { unwrapResult } from "@reduxjs/toolkit";
import { debounce, isEmpty, isEqual } from "lodash";
import { defaultPermission, tableHeaderConfig } from "./config";
import {
    convertClientHubList,
    ConvertedClientHub,
    getPermissionsByType,
    normalizeHubManagementFilters,
} from "./adapters";

// Constants
import { initialPagination } from "constants/constants";

// Types
import { SelectChangeEvent } from "@mui/material";
import { OrderTable, OrderTableBy } from "components/SortMenu/SortMenu";
import { urlPaths } from "enums/urlPaths";

type UsersData = { id: number; term: string; subTerm?: string; value: string };
type Filters = { filter_name: string; filter_roles_ld: string[]; filter_roles_pace: string[] };

const DEFAULT_PAGE_SIZE = 24;
const FILTERS_INITIAL_VALUE = { filter_name: "", filter_roles_ld: ["all"], filter_roles_pace: ["all"] };
const DEFAULT_SORT = { columnId: "name", direction: "asc" as OrderTable };

const useHubManagement = (): {
    hubs: ConvertedClientHub;
    filters: Filters;
    shouldOpenSnackBar: boolean;
    popOverAnchorEl: HTMLElement | null;
    tableHeader: typeof tableHeaderConfig;
    currentSort: OrderTableBy;
    rowId: number;
    hidableColumns: typeof tableHeaderConfig;
    visibleColumns: (string | number)[];
    disableAll: boolean;
    disableClearAll: boolean;
    ldPermissionsData: RoleBundle[];
    pacePermissionsData: RoleBundle[];
    inputName: string;
    inputPage: number | string;
    paginationTextInputRef: React.MutableRefObject<any>;
    usersData: UsersData[];
    handleChangeFilters: (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string[]>
    ) => void;
    handleClearFilters: (filterName: "filter_roles_ld" | "filter_roles_pace" | "all") => void;
    handleSort: (direction: OrderTable, columnId: string | number) => void;
    handleClickCounter: (ev: React.MouseEvent<HTMLDivElement, MouseEvent>, id: number) => void;
    handleClickCloseSnackbar: (ev: React.MouseEvent<HTMLElement>, reason?: "backdropClick" | "escapeKeyDown") => void;
    handleToggleColumn: (ev: React.MouseEvent<HTMLLIElement, MouseEvent>, itemId: string | number) => void;
    handlePageChange: (page: number) => Promise<void>;
    handleInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleOnBlur: () => void;
    handleOnKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    handleEditClientHub: (clientHubId: number) => void;
    handleAddClientHub: () => void;
} => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    // If user changes current hub and doesn't have permissions, redirect somewhere safe
    useRedirectOnNoAdminPermission("SYS_ADMIN_VIEW_CLIENT_HUB", urlPaths.PaceLabsDashboard);

    const { sessionData } = useAuth();
    const orgId = sessionData?.authn.user.org_selected.id;

    const { shouldOpenSnackBar, popOverAnchorEl, handleOpenSnackBar, handleCloseSnackBar } = useSnackBar();

    const { data: permissionsList } = useAppSelector(getPermissionsRefBundle);

    const ldPermissionsData = [defaultPermission, ...getPermissionsByType(permissionsList, "LD")];
    const pacePermissionsData = [defaultPermission, ...getPermissionsByType(permissionsList, "PACE")];

    const [hubs, setHubs] = useState<ConvertedClientHub>({
        data: [],
        meta: initialPagination,
        status: "pristine",
    });

    const [inputName, setInputName] = useState("");
    const [filters, setFilters] = useState<Filters>(FILTERS_INITIAL_VALUE);
    const [tableHeader, setTableHeader] = useState(tableHeaderConfig);
    const [currentSort, setCurrentSort] = useState<OrderTableBy>(DEFAULT_SORT);
    const [rowId, setRowId] = useState(0);
    const [usersData, setUsersData] = useState<UsersData[]>([]);

    const previousFilters = useRef(FILTERS_INITIAL_VALUE);
    const hidableColumns = tableHeader.filter((cell) => cell.hasOwnProperty("isHidden"));
    const visibleColumns = hidableColumns.filter((cell) => cell.isHidden).map((col) => col.id);

    const [inputPage, setInputPage] = useState<number | string>(1);
    const previousPageValue = useRef<number | string>(1);
    const paginationTextInputRef = useRef<any>(null);

    const currentSortValue = { order_by: `${currentSort.columnId},${currentSort.direction}` };
    const disableAll = hubs.status === "fetching";
    const disableClearAll =
        disableAll ||
        Boolean(
            !Object.values(filters)
                .flat()
                .filter((el) => el !== "" && el.toLowerCase() !== "all").length
        );

    const handleFetchData = async (params: Partial<TClientHubFiltersPayload>, permissionsList: RoleBundleResponse) => {
        if (isEqual(filters, previousFilters.current) && isEmpty(params)) {
            return;
        }

        const requestPayload = {
            page_size: DEFAULT_PAGE_SIZE,
            page_no: params?.page_no || 1,
            ...(params
                ? normalizeHubManagementFilters(
                      params,
                      [defaultPermission, ...getPermissionsByType(permissionsList, "LD")],
                      [defaultPermission, ...getPermissionsByType(permissionsList, "PACE")]
                  )
                : {}),
        };

        try {
            setHubs((hubs) => ({ ...hubs, status: "fetching" }));
            const { data } = await fetchClientHub(requestPayload);
            const convertedData = convertClientHubList(data.results, permissionsList);
            setHubs((hubs) => ({ ...hubs, data: convertedData, meta: data.meta }));
            previousFilters.current = isEmpty(params) ? filters : FILTERS_INITIAL_VALUE;
        } catch (error) {
            console.error(error);
        } finally {
            setHubs((hubs) => ({ ...hubs, status: "done" }));
        }
    };

    // --------------------------------------------------------------------------------------------------------------
    // Filters and Sorting Functions
    // --------------------------------------------------------------------------------------------------------------
    const delayedFilterName = useRef(
        debounce((value: string, permissionsList: RoleBundleResponse, filters: Filters) => {
            handleFetchData(
                { ...filters, ...currentSortValue, filter_name: value, page_size: DEFAULT_PAGE_SIZE, page_no: 1 },
                permissionsList
            );
        }, 500)
    ).current;
    useEffect(() => () => delayedFilterName.cancel(), [delayedFilterName]);

    const handleChangeFilters = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string[]>
    ) => {
        setInputPage(1);

        if (event.target.name === "hubname-filter") {
            const newValue = (event as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>).target.value;
            setInputName(newValue);
            setFilters((currentVal) => ({ ...currentVal, filter_name: newValue }));
            delayedFilterName(newValue, permissionsList, filters);
        }

        if (event.target.name === "ld-permission-filter") {
            let newValue = event.target.value as string[];

            // If last selected value all (default), reset selection to default
            if ((newValue.length > 1 && newValue.at(-1) === "all") || !newValue.length) {
                handleClearFilters("filter_roles_ld");
                return;
            }

            newValue = newValue.filter((value) => value !== "all");
            setFilters((currentVal) => ({ ...currentVal, filter_roles_ld: newValue }));

            handleFetchData(
                {
                    ...filters,
                    ...currentSortValue,
                    filter_roles_ld: newValue.map((val) => ldPermissionsData.find((p) => p.name === val)?.name || ""),
                },
                permissionsList
            );
        }

        if (event.target.name === "pace-permission-filter") {
            let newValue = event.target.value as string[];

            // If last selected value is all (default), reset selection to default
            if ((newValue.length > 1 && newValue.at(-1) === "all") || !newValue.length) {
                handleClearFilters("filter_roles_pace");
                return;
            }

            newValue = newValue.filter((value) => value !== "all");
            setFilters((currentVal) => ({ ...currentVal, filter_roles_pace: newValue }));

            handleFetchData(
                {
                    ...filters,
                    ...currentSortValue,
                    filter_roles_pace: newValue.map(
                        (val) => pacePermissionsData.find((p) => p.name === val)?.name || ""
                    ),
                },
                permissionsList
            );
        }
    };

    const handleClearFilters = (filterName: "filter_roles_ld" | "filter_roles_pace" | "all") => {
        if (filterName === "all") {
            setInputName("");
            setInputPage(1);
            setFilters(FILTERS_INITIAL_VALUE);
            setCurrentSort(DEFAULT_SORT);
            handleFetchData(
                { ...FILTERS_INITIAL_VALUE, order_by: `${DEFAULT_SORT.columnId},${DEFAULT_SORT.direction}` },
                permissionsList
            );
            return;
        }
        if (filterName) {
            setFilters((currentVal) => ({ ...currentVal, [filterName]: ["all"] }));
            handleFetchData({ ...filters, ...currentSortValue, [filterName]: [] }, permissionsList);
        }
    };

    const handleSort = (direction: OrderTable, columnId: string | number) => {
        setCurrentSort({ columnId, direction });
        handleFetchData(
            {
                ...filters,
                page_size: hubs.meta.page_size,
                page_no: typeof inputPage === "string" ? parseInt(inputPage || "1") : inputPage,
                order_by: `${columnId},${direction}`,
            },
            permissionsList
        );
    };

    // --------------------------------------------------------------------------------------------------------------
    // Other Functions
    // --------------------------------------------------------------------------------------------------------------
    const handleClickCounter = (ev: React.MouseEvent<HTMLDivElement, MouseEvent>, id: number) => {
        handleOpenSnackBar(ev);
        setRowId(id);

        const users = hubs.data.filter((hub) => hub.id === id)[0].users;
        setUsersData(users);
    };

    const handleClickCloseSnackbar = (
        ev: React.MouseEvent<HTMLElement>,
        reason?: "backdropClick" | "escapeKeyDown"
    ) => {
        handleCloseSnackBar(ev, reason, () => {
            setRowId(0);
        });
    };

    const handleToggleColumn = (ev: React.MouseEvent<HTMLLIElement, MouseEvent>, itemId: string | number) => {
        setTableHeader((prevVal) =>
            prevVal.map((cell) => {
                if (cell.hasOwnProperty("isHidden") && cell.id === itemId) {
                    return { ...cell, isHidden: !cell.isHidden };
                }
                return cell;
            })
        );
    };

    const handleEditClientHub = (clientHubId: number) => {
        navigate(`${urlPaths.HubManagementEditClient}/${clientHubId}`);
    };

    const handleAddClientHub = () => {
        navigate(`${urlPaths.HubManagementNew}`);
    };

    // --------------------------------------------------------------------------------------------------------------
    // Pagination functions
    // --------------------------------------------------------------------------------------------------------------
    const handlePageChange = async (page: number) => {
        if (page < 1 || page > hubs.meta.pages) {
            return;
        }
        setInputPage(page);
        handleFetchData(
            {
                ...filters,
                ...currentSortValue,
                page_size: hubs.meta.page_size,
                page_no: page,
            },
            permissionsList
        );
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.value === "") {
            setInputPage("");
            return;
        }
        const inputValue = Number(event.target.value);
        if (inputValue > 0 && inputValue <= hubs.meta.pages) {
            setInputPage(inputValue);
        }
    };

    const handleOnKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            if (inputPage === hubs.meta.page_no) {
                return;
            }

            paginationTextInputRef.current.blur();
        }

        if (event.key === "Escape") {
            setInputPage(previousPageValue.current || 1);
            setTimeout(() => {
                paginationTextInputRef.current.blur();
            }, 1);
            return;
        }
    };

    const handleOnBlur = () => {
        if (inputPage === "" || inputPage === hubs.meta.page_no) {
            setInputPage(hubs.meta.page_no);
            return;
        }

        if (Boolean(inputPage) && Number(inputPage) > 0 && Number(inputPage) <= hubs.meta.pages) {
            previousPageValue.current = inputPage;
            handlePageChange(Number(inputPage));
        }
    };

    // --------------------------------------------------------------------------------------------------------------
    // Initial Fetch
    // --------------------------------------------------------------------------------------------------------------
    useEffect(() => {
        const initialFetch = async () => {
            dispatch(toggleLoader(true));
            let permissions = {} as RoleBundleResponse;

            // If user changes current selected hub, reset filters and pagination related
            previousFilters.current = FILTERS_INITIAL_VALUE;
            previousPageValue.current = 1;

            setFilters(FILTERS_INITIAL_VALUE);
            setCurrentSort(DEFAULT_SORT);
            setInputName("");
            setInputPage(1);
            setRowId(0);
            // ---------------------------------------------------

            try {
                permissions = unwrapResult(await dispatch(fetchPermissionsRefBundleThunk()));
            } catch (error) {
                console.error(error);
            }

            try {
                const { data } = await fetchClientHub({
                    page_size: DEFAULT_PAGE_SIZE,
                    order_by: `${currentSort.columnId},${currentSort.direction}`,
                });
                const convertedData = convertClientHubList(data.results, permissions);
                setHubs({ ...data, data: convertedData, status: "done" });
            } catch (error) {
                console.error(error);
            } finally {
                dispatch(toggleLoader(false));
            }
        };

        initialFetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgId]);

    return {
        hubs,
        filters,
        shouldOpenSnackBar,
        popOverAnchorEl,
        tableHeader,
        currentSort,
        rowId,
        hidableColumns,
        visibleColumns,
        disableAll,
        disableClearAll,
        ldPermissionsData,
        pacePermissionsData,
        inputName,
        inputPage,
        paginationTextInputRef,
        usersData,
        handleChangeFilters,
        handleClearFilters,
        handleSort,
        handleClickCounter,
        handleClickCloseSnackbar,
        handleToggleColumn,
        handlePageChange,
        handleInputChange,
        handleOnBlur,
        handleOnKeyDown,
        handleEditClientHub,
        handleAddClientHub,
    };
};

export default useHubManagement;
